import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';

const LOGO_URL = '/logo.svg';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { isAuthenticated, signOut } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { businessName } = useBusinessSettings();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/sales', label: 'Homes for Sale' },
    { to: '/sellers', label: 'For Sellers' },
    { to: '/buyers', label: 'For Buyers' },
    { to: '/team', label: 'Our Team' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ];

  const portalPath = isAuthenticated ? '/portal/admin' : '/login';

  const isTransparent = isHomePage && !isScrolled && !isMobileMenuOpen;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? 'bg-transparent text-white'
          : 'bg-white/95 backdrop-blur-sm text-dark-900 shadow-sm'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <img
              src={LOGO_URL}
              alt={businessName}
              className="w-8 h-8 md:w-9 md:h-9 object-contain"
            />
            <span className="font-display font-bold text-lg md:text-xl tracking-tight">
              {businessName}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              const active = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`group relative text-sm font-medium transition-colors hover:text-gold-500 whitespace-nowrap ${
                    active ? 'text-gold-600' : ''
                  }`}
                >
                  {link.label}
                  <span
                    className={`pointer-events-none absolute -bottom-1.5 left-0 h-0.5 bg-gold-500 transition-all duration-300 ${
                      active ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </Link>
              );
            })}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                isTransparent
                  ? 'text-white/80 hover:text-white hover:bg-white/10'
                  : 'text-dark-500 hover:text-dark-700 hover:bg-warm-100 dark:text-dark-300 dark:hover:text-white dark:hover:bg-dark-700'
              }`}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <i className={`${isDark ? 'ri-sun-line' : 'ri-moon-line'} text-sm`}></i>
            </button>
            {isAuthenticated ? (
              <>
                <Link
                  to={portalPath}
                  className={`text-sm font-medium transition-colors hover:text-brand-500 whitespace-nowrap ${
                    isTransparent ? 'text-white' : 'text-dark-700'
                  }`}
                >
                  My Portal
                </Link>
                <button
                  onClick={() => signOut()}
                  className="px-5 py-2.5 bg-gold-500 text-white text-sm font-medium rounded-full hover:bg-gold-600 transition-colors whitespace-nowrap"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`text-sm font-medium transition-colors hover:text-gold-500 whitespace-nowrap ${
                    isTransparent ? 'text-white' : 'text-slate-600'
                  }`}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="px-5 py-2.5 bg-gold-500 text-white text-sm font-medium rounded-full hover:bg-gold-600 transition-colors whitespace-nowrap"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <i
              className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl`}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-warm-200 animate-fade-up">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  location.pathname === link.to
                    ? 'bg-gold-50 text-gold-700'
                    : 'text-dark-700 hover:bg-warm-100'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-warm-200 space-y-2">
              {isAuthenticated ? (
                <>
                  <Link
                    to={portalPath}
                    className="block px-4 py-3 text-sm font-medium text-dark-700"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    My Portal
                  </Link>
                  <button
                    onClick={() => { signOut(); setIsMobileMenuOpen(false); }}
                    className="block w-full text-left px-4 py-3 text-sm font-medium text-dark-700"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block px-4 py-3 text-sm font-medium text-dark-700"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="block mx-4 py-3 bg-brand-500 text-white text-sm font-medium rounded-full text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}